import { MoreVert, Save } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useMutateDeleteCropMonitoringNote, useMutateUpdateCropMonitoringNote } from '/@/hooks/crop-monitoring-notes';
import { _ } from '/@/locale';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { CropMonitoringNote } from '/@/types/crop-monitoring-note';
import { formatDate } from '/@/utils';
import { recordOrigin, trackRegistryChange } from './add/flow-tracking';
import { getUserDisplayName, RegistryFooter, RegistryFooterActions } from './card-footer';
import { ConfirmDeleteModal } from './confirm-delete-modal';
import { EditActions } from './edit-actions';
import { RegistryCard } from './registry-card';

const Note = ({
    note,
    cropMonitoring,
    expanded,
    onToggle,
}: {
    note: CropMonitoringNote;
    /** The campaign's crop monitoring: gives the tracked events their crop and phenology. */
    cropMonitoring?: CropMonitoring;
    /** Mobile-only collapse state, injected by `Registries`' accordion. */
    expanded?: boolean;
    onToggle?: () => void;
}) => {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [content, setContent] = useState(note.content ?? '');
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    const updateNote = useMutateUpdateCropMonitoringNote();
    const deleteNote = useMutateDeleteCropMonitoringNote();

    const authorName = getUserDisplayName(note.createdBy);

    const registeredAt = formatDate(new Date(note.created));

    const closeMenu = () => setMenuAnchor(null);
    const tracked = {
        fieldId: note.field,
        cropMonitoring,
        registryType: 'note' as const,
        registryOrigin: recordOrigin({}),
    };

    const startEditing = () => {
        setContent(note.content ?? '');
        setIsEditing(true);
        closeMenu();
    };

    const cancelEditing = () => {
        setContent(note.content ?? '');
        setIsEditing(false);
    };

    const handleSave = async () => {
        if (!content.trim()) return;
        await updateNote.trigger({ id: note.id, content: content.trim() });
        trackRegistryChange('Field Registry Edited', tracked);
        setIsEditing(false);
    };

    const startDeleting = () => {
        closeMenu();
        setConfirmingDelete(true);
    };

    return (
        <>
            <RegistryCard
                header={
                    <Typography variant='h6' color='primary'>
                        {_('Nota')}
                    </Typography>
                }
                menu={
                    // Mobile shows visible Eliminar/Editar buttons in the body instead (see
                    // below) — no "more options" dropdown there, matching the Figma mock.
                    onToggle ? undefined : (
                        <>
                            <IconButton
                                size='small'
                                aria-label={_('Más opciones')}
                                onClick={(event) => setMenuAnchor(event.currentTarget)}
                            >
                                <MoreVert />
                            </IconButton>
                            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                                <MenuItem onClick={startEditing}>{_('Editar')}</MenuItem>
                                <MenuItem onClick={startDeleting} disabled={deleteNote.isMutating}>
                                    {_('Eliminar')}
                                </MenuItem>
                            </Menu>
                        </>
                    )
                }
                footer={
                    <RegistryFooter
                        authorName={authorName}
                        date={registeredAt}
                        actions={
                            onToggle && expanded && !isEditing ? (
                                <RegistryFooterActions
                                    onEdit={startEditing}
                                    onDelete={startDeleting}
                                    deleteDisabled={deleteNote.isMutating}
                                />
                            ) : undefined
                        }
                    />
                }
                expanded={expanded}
                onToggle={onToggle}
            >
                {isEditing ? (
                    <Stack spacing={1} width='100%'>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            value={content}
                            onChange={(event) => setContent(event.target.value)}
                            size='small'
                        />
                        <EditActions
                            onCancel={cancelEditing}
                            onSave={handleSave}
                            saveDisabled={!content.trim() || updateNote.isMutating}
                            saveEndIcon={<Save sx={{ fontSize: '12px !important' }} />}
                        />
                    </Stack>
                ) : (
                    note.content && (
                        <Typography variant='body2' color='primary'>
                            {note.content}
                        </Typography>
                    )
                )}
            </RegistryCard>
            <ConfirmDeleteModal
                open={confirmingDelete}
                onClose={() => setConfirmingDelete(false)}
                onConfirm={async () => {
                    await deleteNote.trigger({ id: note.id });
                    trackRegistryChange('Field Registry Deleted', tracked);
                }}
                title={_('¿Eliminar la nota?')}
                message={_('Esta acción no se puede deshacer.')}
                isDeleting={deleteNote.isMutating}
            />
        </>
    );
};

export default Note;
